import React from 'react';
import { Modal, Button, VStack, HStack, Box, Text, Pressable } from 'native-base';
import MaterialIconByName from '../../MaterialIconByName';
import { isElectron } from 'src/utils/miscUtils';
import { getText } from 'src/utils/i18n';

export type ReportFormat = 'pdf' | 'html' | 'json' | 'xls' | 'thermal_58mm';

interface ReportFormatOption {
    value: ReportFormat;
    label: string;
    description: string;
    icon: string;
    iconColor?: string;
}

interface ReportFormatSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectFormat: (format: ReportFormat) => void;
    isLoading?: boolean;
}

const getFormatOptions = (): ReportFormatOption[] => {
    const options: ReportFormatOption[] = [
        {
            value: 'pdf',
            label: 'PDF',
            description: 'Print-ready report with professional layout',
            icon: 'file-pdf-box',
            iconColor: '#DC2626'
        },
        {
            value: 'html',
            label: 'HTML',
            description: 'Web-viewable report that can be opened in any browser',
            icon: 'language-html5',
            iconColor: '#EA580C'
        },
        {
            value: 'json',
            label: 'JSON',
            description: 'Machine-readable data for API integration',
            icon: 'code-braces',
            iconColor: '#7C3AED'
        },
        {
            value: 'xls',
            label: 'Excel',
            description: 'Multi-sheet workbook for data analysis',
            icon: 'file-excel-box',
            iconColor: '#16A34A'
        }
    ];

    // Desktop (Electron) only: print directly to a 58mm/2" thermal printer via OS printer drivers.
    if (isElectron()) {
        options.unshift({
            value: 'thermal_58mm',
            label: 'Thermal (2")',
            description: 'Print to a 58mm thermal printer (USB) via desktop print dialog',
            icon: 'printer',
            iconColor: '#0F766E'
        });
    }

    return options;
};

export default function ReportFormatSelectionModal({ 
    isOpen, 
    onClose, 
    onSelectFormat,
    isLoading = false
}: ReportFormatSelectionModalProps) {
    const [selectedFormat, setSelectedFormat] = React.useState<ReportFormat>('pdf');
    const formatOptions = React.useMemo(() => getFormatOptions(), []);

    React.useEffect(() => {
        if (isOpen) {
            setSelectedFormat(isElectron() ? 'thermal_58mm' : 'pdf');
        }
    }, [isOpen]);

    const handleDownload = () => {
        onSelectFormat(selectedFormat);
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            onClose={onClose}>
            <Modal.Content maxWidth="500px" width="90%">
                <Modal.CloseButton />
                <Modal.Header>{getText('selectReportFormat')}</Modal.Header>
                <Modal.Body>
                    <VStack space={2.5}>
                        {formatOptions.map((option) => {
                            const isSelected = selectedFormat === option.value;
                            return (
                                <Pressable
                                    key={option.value}
                                    onPress={() => setSelectedFormat(option.value)}
                                >
                                    <Box
                                        borderWidth={2}
                                        borderColor={isSelected ? "primary.500" : "gray.200"}
                                        borderRadius="lg"
                                        p={3}
                                        bg={isSelected ? "primary.50" : "white"}
                                        shadow={isSelected ? "2" : "0"}
                                    >
                                        <HStack space={2.5} alignItems="flex-start">
                                            <Box mt={0.5}>
                                                <MaterialIconByName
                                                    name={option.icon}
                                                    size="18"
                                                    color={option.iconColor}
                                                />
                                            </Box>
                                            <VStack flex={1} space={0.5}>
                                                <Text fontWeight="600" fontSize="md" color="gray.800">
                                                    {option.label}
                                                </Text>
                                                <Text fontSize="xs" color="gray.600" lineHeight="xs">
                                                    {option.description}
                                                </Text>
                                            </VStack>
                                            <Box mt={0.5} width="14px" height="14px">
                                                {isSelected && (
                                                    <MaterialIconByName
                                                        name="check-circle"
                                                        size="14"
                                                        color="#3B82F6"
                                                    />
                                                )}
                                            </Box>
                                        </HStack>
                                    </Box>
                                </Pressable>
                            );
                        })}
                    </VStack>
                </Modal.Body>
                <Modal.Footer>
                    <Button.Group space={2}>
                        <Button 
                            variant="ghost" 
                            colorScheme="blueGray"
                            onPress={onClose}
                            isDisabled={isLoading}
                        >
                            {getText('cancel')}
                        </Button>
                        <Button 
                            onPress={handleDownload}
                            isLoading={isLoading}
                            isLoadingText={selectedFormat === 'thermal_58mm' ? getText('openingPrint') : getText('loading')}
                            startIcon={<MaterialIconByName name={selectedFormat === 'thermal_58mm' ? "printer" : "download"} />}
                        >
                            {selectedFormat === 'thermal_58mm' ? getText('print') : getText('download')}
                        </Button>
                    </Button.Group>
                </Modal.Footer>
            </Modal.Content>
        </Modal>
    );
}
